/**
 * GISParcelSelector Component
 * Step-based selector: Sachivalayam → Asset Category → Parcels on Map
 * All parcels plotted on map with hover sync between list and map
 */

import { useState, useEffect, useRef, useCallback } from 'react';
import { createPortal } from 'react-dom';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import axiosInstance from '@/services/interceptor';
import { BASE_LAYERS } from './dashboard/MapSettings';
import WorkDetailsCard from './cards/WorkDetailsCard';
import { WMS_POPUP_HEADER, WMS_POPUP_TITLE, WMS_POPUP_CLOSE } from './common/wmsPopupHeader';
import { ListSkeleton } from '@/components/base/Skeleton';

const BASE_URL = 'ds-wms';

interface Sachivalayam { svm_id: number; svm_cd: string; svm_nm: string; }
interface Parcel { id: number; name: string; category?: string; categoryId?: number; department?: string; location?: string; surveyArea?: number; roadType?: string; }

type AssetCategory = 'roads' | 'water_bodies' | 'public_infra' | 'land_parcels';

interface ExistingMapping {
  wrk_prcl_rel_id: number;
  prcl_id: number;
  prcl_ctgry_cd: string;
  prcl_nm: string;
  prcl_sub_ctgry: string;
  svm_nm: string;
  road_mode_cd: string;
  i_ts: string;
}

interface GISParcelSelectorProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (parcels: { category: AssetCategory; svmId: number; svmName: string; roadMode?: string; items: Parcel[] }) => void;
  udaId: number;
  udaName?: string;
  workId?: number;
  workCode?: string;
  workName?: string;
  workStatus?: string;
  workType?: string;
  workCategory?: string;
  schemeName?: string;
  sanctionAmount?: number;
  workSachivalayams?: Sachivalayam[];
  saving?: boolean;
  onOpenWorkProfile?: (workId: number) => void;
}

const CATEGORY_CONFIG: Record<AssetCategory, { label: string; icon: string; color: string; bgClass: string; mapColor: string }> = {
  roads:        { label: 'Roads',                       icon: 'ri-route-line',       color: 'text-blue-600',  bgClass: 'bg-blue-50 border-blue-200 hover:bg-blue-100',   mapColor: '#3b82f6' },
  water_bodies: { label: 'Water Bodies',                icon: 'ri-water-flash-line', color: 'text-cyan-600',  bgClass: 'bg-cyan-50 border-cyan-200 hover:bg-cyan-100',   mapColor: '#06b6d4' },
  public_infra:  { label: 'Public Infra / Govt Assets',  icon: 'ri-government-line',  color: 'text-amber-600',  bgClass: 'bg-amber-50 border-amber-200 hover:bg-amber-100',   mapColor: '#d97706' },
  land_parcels:  { label: 'Land Parcels',                icon: 'ri-landscape-line',   color: 'text-violet-600', bgClass: 'bg-violet-50 border-violet-200 hover:bg-violet-100', mapColor: '#7c3aed' },
};

// Water body category ID in GIS DB
const WATER_BODY_CATEGORY_ID = 98;
const PUBLIC_INFRA_CATEGORY_IDS = [82,83,86,88,89,90,91,92,93,94,95,96,97,100,101,102,103];

// Land parcel sub-category colors by category_id
const LAND_PARCEL_COLORS: Record<number, { color: string; fill: string; label: string }> = {
  78:  { color: '#15803d', fill: '#86efac', label: 'Agriculture Land' },      // green (strong)
  77:  { color: '#a16207', fill: '#fde68a', label: 'Vacant Land' },           // amber
  105: { color: '#6d28d9', fill: '#c4b5fd', label: 'Land with Structures' },  // violet
  98:  { color: '#0891b2', fill: '#a5f3fc', label: 'Water Bodies' },          // cyan
  106: { color: '#c2410c', fill: '#fed7aa', label: 'Public Infra' },          // orange
};
const LAND_PARCEL_DEFAULT_COLOR = { color: '#64748b', fill: '#e2e8f0' };   // slate fallback

// Road type colors by construction material name
const ROAD_TYPE_COLORS: Record<string, { color: string; label: string }> = {
  'Concrete':     { color: '#6366f1', label: 'Concrete' },      // indigo
  'Asphalt':      { color: '#0f172a', label: 'Asphalt' },       // dark slate
  'BT':           { color: '#7c3aed', label: 'BT' },            // violet
  'WBM':          { color: '#0891b2', label: 'WBM' },           // cyan
  'Paver Blocks': { color: '#c026d3', label: 'Paver Blocks' },  // fuchsia
  'Gravel':       { color: '#a16207', label: 'Gravel' },        // amber
  'Earthen':      { color: '#92400e', label: 'Earthen' },       // brown
  'Any Other':    { color: '#64748b', label: 'Any Other' },     // slate
};
const ROAD_DEFAULT_COLOR = '#64748b';

export default function GISParcelSelector({
  isOpen, onClose, onSave, udaId, udaName, workId, workCode, workName,
  workStatus, workType, workCategory, schemeName, sanctionAmount,
  workSachivalayams = [], saving = false, onOpenWorkProfile,
}: GISParcelSelectorProps) {
  const [isFullscreen, setIsFullscreen] = useState(false);

  // Invalidate map size when fullscreen toggles
  useEffect(() => {
    if (mapRef.current) {
      setTimeout(() => mapRef.current?.invalidateSize(), 350);
    }
  }, [isFullscreen]);

  const [step, setStep] = useState(1);
  const [selectedSvm, setSelectedSvm] = useState<Sachivalayam | null>(null);
  const [selectedCategory, setSelectedCategory] = useState<AssetCategory | null>(null);
  // For roads: 'existing' = select from existing roads, 'proposed' = draw new, 'both' = both options
  const [roadMode, setRoadMode] = useState<'existing' | 'proposed' | 'both' | null>(null);

  // Proposed road drawing state
  const ROAD_CONSTRUCTION_TYPES = [
    { id: 1, name: 'Any Other' }, { id: 2, name: 'Asphalt' }, { id: 5, name: 'BT' },
    { id: 3, name: 'Concrete' }, { id: 7, name: 'Earthen' }, { id: 6, name: 'Gravel' },
    { id: 8, name: 'Paver Blocks' }, { id: 4, name: 'WBM' },
  ];
  const [drawnRoads, setDrawnRoads] = useState<{ id: number; name: string; constructionType: string; latlngs: [number, number][]; length: number }[]>([]);
  const [drawingActive, setDrawingActive] = useState(false);
  const [showProposedForm, setShowProposedForm] = useState(false);
  const [newRoadName, setNewRoadName] = useState('');
  const [newRoadType, setNewRoadType] = useState('Concrete');
  const [currentDrawnLatLngs, setCurrentDrawnLatLngs] = useState<[number, number][]>([]);
  const drawLayerRef = useRef<L.Polyline | null>(null);
  const drawnLayersRef = useRef<Map<number, L.Polyline>>(new Map());
  const drawIdCounter = useRef(0);

  // Legend filter: hidden types (road_type names or category_id strings)
  const [hiddenLegendKeys, setHiddenLegendKeys] = useState<Set<string>>(new Set());

  const [existingMappings, setExistingMappings] = useState<ExistingMapping[]>([]);
  const [existingView, setExistingView] = useState<'list' | 'map'>('list');
  const existingMapContainerRef = useRef<HTMLDivElement>(null);
  const existingMapRef = useRef<L.Map | null>(null);

  const [allSachivalayams, setAllSachivalayams] = useState<Sachivalayam[]>([]);
  const [svmLoading, setSvmLoading] = useState(false);
  const [svmSearch, setSvmSearch] = useState('');
  const [infraSummary, setInfraSummary] = useState<any>(null);

  const [parcels, setParcels] = useState<Parcel[]>([]);
  const [parcelsLoading, setParcelsLoading] = useState(false);
  const [selectedParcels, setSelectedParcels] = useState<Set<number>>(new Set());
  const [hoveredParcelId, setHoveredParcelId] = useState<number | null>(null);

  const [splitPercent, setSplitPercent] = useState(40);
  const splitContainerRef = useRef<HTMLDivElement>(null);
  const [baseLayerKey, setBaseLayerKey] = useState('osm');
  const tileLayerRef = useRef<L.TileLayer | null>(null);

  const mapContainerRef = useRef<HTMLDivElement>(null);
  const mapRef = useRef<L.Map | null>(null);
  const parcelLayersMap = useRef<Map<number, L.Path>>(new Map());
  const parcelCategoryMap = useRef<Map<number, number>>(new Map()); // parcel id → category_id
  const parcelRoadTypeMap = useRef<Map<number, string>>(new Map()); // parcel id → road_type
  const listItemRefsMap = useRef<Map<number, HTMLLabelElement>>(new Map());
  const loadedGeoJsonRef = useRef<any>(null); // cached GeoJSON for map to reuse
  // svm_cd → GIS svm_id lookup (GIS DB IDs differ from maud_gen IDs)
  const [gisSvmIdMap, setGisSvmIdMap] = useState<Map<string, number>>(new Map());
  const boundaryGeoJsonRef = useRef<any>(null);

  const getGisSvmId = (svm: Sachivalayam): number => {
    return gisSvmIdMap.get(String(svm.svm_cd)) || svm.svm_id;
  };

  const handleDragStart = useCallback((e: React.MouseEvent) => {
    e.preventDefault();
    const container = splitContainerRef.current;
    if (!container) return;
    const onMove = (ev: MouseEvent) => {
      const rect = container.getBoundingClientRect();
      const pct = ((ev.clientX - rect.left) / rect.width) * 100;
      setSplitPercent(Math.min(70, Math.max(20, pct)));
    };
    const onUp = () => {
      document.removeEventListener('mousemove', onMove);
      document.removeEventListener('mouseup', onUp);
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
      mapRef.current?.invalidateSize();
    };
    document.body.style.cursor = 'col-resize';
    document.body.style.userSelect = 'none';
    document.addEventListener('mousemove', onMove);
    document.addEventListener('mouseup', onUp);
  }, []);

  useEffect(() => {
    if (isOpen) {
      setIsFullscreen(false); setStep(1); setSelectedSvm(null); setSelectedCategory(null); setRoadMode(null);
      setParcels([]); setSelectedParcels(new Set()); setSvmSearch('');
      setDrawnRoads([]); setDrawingActive(false); setShowProposedForm(false); setNewRoadName(''); setNewRoadType('Concrete'); setCurrentDrawnLatLngs([]);
      drawIdCounter.current = 0; drawnLayersRef.current.clear();
      setInfraSummary(null); setHoveredParcelId(null);
      listItemRefsMap.current.clear(); parcelLayersMap.current.clear(); parcelCategoryMap.current.clear(); parcelRoadTypeMap.current.clear();
      setGisSvmIdMap(new Map()); boundaryGeoJsonRef.current = null;
      setExistingMappings([]); setExistingView('list');
      if (existingMapRef.current) { existingMapRef.current.remove(); existingMapRef.current = null; }
    }
  }, [isOpen]);

  // Load existing parcel mappings for this work
  useEffect(() => {
    if (!isOpen || !workId) return;
    axiosInstance.get(`${BASE_URL}/parcel-mapping/work/${workId}`)
      .then(res => setExistingMappings(res.data?.data || []))
      .catch(() => setExistingMappings([]));
  }, [isOpen, workId]);

  // Initialize map for existing mapped geographies
  useEffect(() => {
    if (existingView !== 'map' || !existingMapContainerRef.current || existingMappings.length === 0 || !selectedSvm) return;

    const timer = setTimeout(() => {
      if (existingMapRef.current || !existingMapContainerRef.current) return;

      const baseLayer = BASE_LAYERS[0];
      const map = L.map(existingMapContainerRef.current, { center: [15.9, 79.7], zoom: 12, zoomControl: true });
      L.tileLayer(baseLayer.url, { attribution: baseLayer.attribution, maxZoom: baseLayer.maxZoom || 19 }).addTo(map);
      existingMapRef.current = map;

      // Show sachivalayam boundary
      const cachedGeo = boundaryGeoJsonRef.current;
      if (cachedGeo?.features) {
        const svmFeature = cachedGeo.features.find((f: any) => String(f.properties?.svm_cd) === String(selectedSvm.svm_cd));
        if (svmFeature) {
          const bndLayer = L.geoJSON(svmFeature, {
            style: { color: '#6366f1', weight: 2, fillColor: '#6366f1', fillOpacity: 0.03, dashArray: '5,5' },
          }).addTo(map);
          map.fitBounds(bndLayer.getBounds(), { padding: [30, 30] });
        }
      }

      // Build set of mapped parcel IDs by category
      const mappedIds = new Set(existingMappings.map(m => Number(m.prcl_id)));
      const mappedCategories = new Set(existingMappings.map(m => m.prcl_ctgry_cd));
      const gisSvmId = getGisSvmId(selectedSvm);

      // Load GeoJSON for each mapped category and highlight matched parcels
      const loads: Promise<void>[] = [];

      if (mappedCategories.has('roads')) {
        loads.push(
          axiosInstance.get(`${BASE_URL}/dashboard/road-network`, { params: { svmId: gisSvmId } }).then(res => {
            const geojson = res.data?.data || res.data;
            if (!geojson?.features) return;
            // Show all roads faintly, highlight mapped ones
            L.geoJSON(geojson, {
              style: (feature) => {
                const id = Number(feature?.properties?.ds_lyr_id || feature?.properties?.road_id);
                const isMapped = mappedIds.has(id);
                const rt = feature?.properties?.road_type || '';
                const rtc = ROAD_TYPE_COLORS[rt];
                return isMapped
                  ? { color: rtc?.color || '#059669', weight: 4, fillOpacity: 0.3, fill: true }
                  : { color: '#94a3b8', weight: 1, opacity: 0.3 };
              },
              onEachFeature: (feature, fl) => {
                const id = Number(feature?.properties?.ds_lyr_id || feature?.properties?.road_id);
                if (mappedIds.has(id)) {
                  fl.bindTooltip(feature.properties?.road_name || 'Road', { direction: 'center', className: 'text-xs font-medium' });
                }
              },
            }).addTo(map);
          }).catch(() => {})
        );
      }

      if (mappedCategories.has('land_parcels') || mappedCategories.has('water_bodies') || mappedCategories.has('public_infra')) {
        loads.push(
          axiosInstance.get(`${BASE_URL}/dashboard/land-parcels`, { params: { svmId: gisSvmId } }).then(res => {
            const geojson = res.data?.data || res.data;
            if (!geojson?.features) return;
            L.geoJSON(geojson, {
              style: (feature) => {
                const id = Number(feature?.properties?.ds_lyr_id || feature?.properties?.parcel_id);
                const isMapped = mappedIds.has(id);
                if (isMapped) {
                  const cid = Number(feature?.properties?.category_id);
                  const lpc = LAND_PARCEL_COLORS[cid] || LAND_PARCEL_DEFAULT_COLOR;
                  return { color: lpc.color, weight: 3, fillColor: lpc.fill, fillOpacity: 0.5, fill: true };
                }
                return { color: '#94a3b8', weight: 0.5, fillColor: '#e2e8f0', fillOpacity: 0.1, fill: true };
              },
              onEachFeature: (feature, fl) => {
                const id = Number(feature?.properties?.ds_lyr_id || feature?.properties?.parcel_id);
                if (mappedIds.has(id)) {
                  const name = feature.properties?.category || feature.properties?.land_usage || `Parcel ${id}`;
                  fl.bindTooltip(name, { direction: 'center', className: 'text-xs font-medium' });
                }
              },
            }).addTo(map);
          }).catch(() => {})
        );
      }

      Promise.all(loads).then(() => map.invalidateSize());
    }, 150);

    return () => {
      clearTimeout(timer);
      if (existingMapRef.current) { existingMapRef.current.remove(); existingMapRef.current = null; }
    };
  }, [existingView, existingMappings, selectedSvm]);

  // Load sachivalayam list + boundary GeoJSON (for GIS ID lookup)
  useEffect(() => {
    if (!isOpen || !udaId) return;
    setSvmLoading(true);

    Promise.all([
      axiosInstance.get(`${BASE_URL}/sachivalayam-mapping/ulb/${udaId}/sachivalayams`),
      axiosInstance.get(`${BASE_URL}/dashboard/sachivalayam-boundaries`, { params: { udaId } }),
    ]).then(([listRes, bndRes]) => {
      setAllSachivalayams(listRes.data.data || []);
      const geojson = bndRes.data?.data || bndRes.data;
      boundaryGeoJsonRef.current = geojson;
      // Build svm_cd → GIS svm_id map
      if (geojson?.features) {
        const m = new Map<string, number>();
        geojson.features.forEach((f: any) => {
          const p = f.properties || {};
          if (p.svm_cd && p.svm_id) m.set(String(p.svm_cd), Number(p.svm_id));
        });
        setGisSvmIdMap(m);
      }
    }).catch(() => {
      setAllSachivalayams([]);
    }).finally(() => setSvmLoading(false));
  }, [isOpen, udaId]);

  // Load infra summary + land parcel count using GIS svm_id
  useEffect(() => {
    if (!selectedSvm || gisSvmIdMap.size === 0) return;
    setInfraSummary(null);
    const gisSvmId = getGisSvmId(selectedSvm);
    Promise.all([
      axiosInstance.get(`${BASE_URL}/dashboard/ward-infrastructure-summary`, { params: { svmId: gisSvmId } }),
      axiosInstance.get(`${BASE_URL}/dashboard/land-parcels`, { params: { svmId: gisSvmId } }),
    ]).then(([infraRes, parcelsRes]) => {
      const summary = infraRes.data?.data || infraRes.data || {};
      const geojson = parcelsRes.data?.data || parcelsRes.data;
      const features = geojson?.features || [];
      // Count from GeoJSON directly (more reliable than infra summary for these)
      const waterCount = features.filter((f: any) => Number(f.properties?.category_id) === WATER_BODY_CATEGORY_ID).length;
      const infraCount = features.filter((f: any) => PUBLIC_INFRA_CATEGORY_IDS.includes(Number(f.properties?.category_id))).length;
      const infraCategories = new Set(features.filter((f: any) => PUBLIC_INFRA_CATEGORY_IDS.includes(Number(f.properties?.category_id))).map((f: any) => f.properties?.category_id));

      // Also count roads from road-network GeoJSON for accuracy
      axiosInstance.get(`${BASE_URL}/dashboard/road-network`, { params: { svmId: gisSvmId } })
        .then(roadRes => {
          const roadGeo = roadRes.data?.data || roadRes.data;
          const roadFeatures = roadGeo?.features || [];
          let totalLen = 0;
          roadFeatures.forEach((f: any) => { totalLen += Number(f.properties?.road_length) || 0; });
          setInfraSummary({
            ...summary,
            roads: { count: roadFeatures.length, totalLength: totalLen },
            waterBodies: { count: waterCount, totalArea: summary?.waterBodies?.totalArea || 0 },
            publicInfra: { totalCount: infraCount, categories: Array.from(infraCategories).map(id => ({ categoryId: id })) },
            landParcels: { count: features.length },
          });
        })
        .catch(() => {
          setInfraSummary({
            ...summary,
            waterBodies: { count: waterCount, totalArea: summary?.waterBodies?.totalArea || 0 },
            publicInfra: { totalCount: infraCount, categories: Array.from(infraCategories).map(id => ({ categoryId: id })) },
            landParcels: { count: features.length },
          });
        });
    }).catch(() => {});
  }, [selectedSvm, gisSvmIdMap]);

  const handleSvmSelect = (svm: Sachivalayam) => {
    setSelectedSvm(svm); setStep(2); setSelectedCategory(null); setRoadMode(null);
    setParcels([]); setSelectedParcels(new Set());
  };

  const handleCategorySelect = (cat: AssetCategory) => {
    if (!selectedSvm) return;
    setSelectedCategory(cat);
    setHiddenLegendKeys(new Set());
    if (cat === 'roads') {
      setRoadMode('both');
      loadParcels('roads');
      return;
    }
    loadParcels(cat);
  };

  const handleRoadModeSelect = (mode: 'existing' | 'proposed' | 'both') => {
    setRoadMode(mode);
    setDrawnRoads([]); setDrawingActive(false);
    if (mode === 'proposed') {
      setStep(3);
      setParcels([]);
      setParcelsLoading(false);
      return;
    }
    // existing or both — load existing roads
    loadParcels('roads');
  };

  const loadParcels = async (cat: AssetCategory) => {
    if (!selectedSvm) return;
    setStep(3);
    setParcels([]); setSelectedParcels(new Set()); setParcelsLoading(true);
    loadedGeoJsonRef.current = null;
    const gisSvmId = getGisSvmId(selectedSvm);

    try {
      let data: Parcel[] = [];
      if (cat === 'roads') {
        const res = await axiosInstance.get(`${BASE_URL}/dashboard/road-network`, { params: { svmId: gisSvmId } });
        const geojson = res.data?.data || res.data;
        loadedGeoJsonRef.current = { type: 'roads', geojson };
        data = (geojson?.features || []).map((f: any) => {
          const p = f.properties || {};
          const len = p.road_length ? `${Number(p.road_length).toFixed(0)}m` : '';
          const widthM = p.road_width ? Number(p.road_width) : 0;
          const width = widthM ? `W: ${widthM.toFixed(1)}m (${(widthM * 3.28084).toFixed(1)}ft)` : '';
          const material = p.road_type || '';
          return {
            id: Number(p.ds_lyr_id || p.road_id),
            name: p.road_name || 'Unnamed Road',
            category: p.road_category || '',
            roadType: p.road_type || '',
            location: [material, len, width].filter(Boolean).join(' · '),
            surveyArea: p.road_length || 0,
          };
        });
      } else {
        // water_bodies, public_infra, land_parcels — all use land-parcels GeoJSON
        const res = await axiosInstance.get(`${BASE_URL}/dashboard/land-parcels`, { params: { svmId: gisSvmId } });
        const geojson = res.data?.data || res.data;
        // Filter features
        const features = (geojson?.features || []).filter((f: any) => {
          if (cat === 'land_parcels') return true;
          const cid = Number(f.properties?.category_id);
          if (cat === 'water_bodies') return cid === WATER_BODY_CATEGORY_ID;
          if (cat === 'public_infra') return PUBLIC_INFRA_CATEGORY_IDS.includes(cid);
          return false;
        });
        // Cache filtered GeoJSON for map
        loadedGeoJsonRef.current = { type: 'parcels', geojson: { ...geojson, features } };
        data = features.map((f: any) => {
          const p = f.properties || {};
          return {
            id: Number(p.ds_lyr_id || p.parcel_id),
            name: p.land_usage || p.category || `Parcel ${p.parcel_id || ''}`,
            category: p.category || '',
            categoryId: Number(p.category_id),
            location: `${p.property_type || ''} · ${(p.parcel_area || 0).toFixed(2)} sq.m`,
            surveyArea: p.parcel_area || 0,
          };
        });
      }
      setParcels(data);
    } catch (e) { console.error('Failed to load parcels:', e); }
    finally { setParcelsLoading(false); }
  };

  // ─── Map initialization ───────────────────────────────────

  useEffect(() => {
    if (step !== 3 || !mapContainerRef.current || !selectedSvm || !selectedCategory) return;

    const timer = setTimeout(() => {
      if (mapRef.current || !mapContainerRef.current) return;

      const baseLayer = BASE_LAYERS.find(l => l.key === baseLayerKey) || BASE_LAYERS[0];
      const map = L.map(mapContainerRef.current, { center: [15.9, 79.7], zoom: 12, zoomControl: true });
      tileLayerRef.current = L.tileLayer(baseLayer.url, { attribution: baseLayer.attribution, maxZoom: baseLayer.maxZoom || 19 }).addTo(map);
      mapRef.current = map;
      parcelLayersMap.current.clear();

      const catColor = CATEGORY_CONFIG[selectedCategory].mapColor;
      const gisSvmId = getGisSvmId(selectedSvm);

      // Use cached boundary GeoJSON for sachivalayam outline
      const cachedGeo = boundaryGeoJsonRef.current;
      if (cachedGeo?.features) {
        const svmFeature = cachedGeo.features.find((f: any) => String(f.properties?.svm_cd) === String(selectedSvm.svm_cd));
        if (svmFeature) {
          const bndLayer = L.geoJSON(svmFeature, {
            style: { color: '#6366f1', weight: 2, fillColor: '#6366f1', fillOpacity: 0.03, dashArray: '5,5' },
          }).addTo(map);
          map.fitBounds(bndLayer.getBounds(), { padding: [30, 30] });
        }
      }

      // Use cached GeoJSON from loadParcels (avoids duplicate API call + ensures same data)
      const addParcelFeatures = (geojson: any, isRoads: boolean) => {
        if (!geojson?.features?.length) return;
        const isLandParcels = selectedCategory === 'land_parcels';
        const geoLayer = L.geoJSON(geojson, {
          style: (feature) => {
            if (isRoads) {
              const roadType = feature?.properties?.road_type || '';
              const rtc = ROAD_TYPE_COLORS[roadType];
              return { color: rtc?.color || ROAD_DEFAULT_COLOR, weight: 3, fillOpacity: 0.15, fill: true };
            }
            if (isLandParcels && feature?.properties?.category_id) {
              const lpc = LAND_PARCEL_COLORS[Number(feature.properties.category_id)] || LAND_PARCEL_DEFAULT_COLOR;
              return { color: lpc.color, weight: 2, fillColor: lpc.fill, fillOpacity: 0.35, fill: true };
            }
            return { color: catColor, weight: 2, fillColor: catColor, fillOpacity: 0.2, fill: true };
          },
          onEachFeature: (feature, fl) => {
            const id = Number(isRoads
              ? (feature.properties?.ds_lyr_id || feature.properties?.road_id)
              : (feature.properties?.ds_lyr_id || feature.properties?.parcel_id));
            parcelLayersMap.current.set(id, fl as L.Path);
            if (feature.properties?.category_id) parcelCategoryMap.current.set(id, Number(feature.properties.category_id));
            if (feature.properties?.road_type) parcelRoadTypeMap.current.set(id, feature.properties.road_type);
            const name = isRoads
              ? (feature.properties?.road_name || 'Road')
              : (feature.properties?.category || feature.properties?.land_usage || `Parcel ${id}`);
            fl.bindTooltip(name, { direction: 'center', className: 'text-xs font-medium' });
            fl.on('click', () => toggleParcel(id));
            fl.on('mouseover', () => { setHoveredParcelId(id); scrollToListItem(id); });
            fl.on('mouseout', () => setHoveredParcelId(p => p === id ? null : p));
          },
        }).addTo(map);
        if (!isRoads) map.fitBounds(geoLayer.getBounds(), { padding: [30, 30] });
      };

      // Wait for loadParcels to finish caching data, then plot
      const waitForData = () => {
        const cached = loadedGeoJsonRef.current;
        if (cached) {
          addParcelFeatures(cached.geojson, cached.type === 'roads');
        } else {
          setTimeout(waitForData, 200);
        }
      };
      // If proposed-only mode, still load road network for context
      if (roadMode === 'proposed') {
        const gisSvmId = getGisSvmId(selectedSvm);
        axiosInstance.get(`${BASE_URL}/dashboard/road-network`, { params: { svmId: gisSvmId } }).then(res => {
          const geojson = res.data?.data || res.data;
          if (geojson?.features?.length) {
            L.geoJSON(geojson, {
              style: () => ({ color: '#94a3b8', weight: 1.5, opacity: 0.5 }),
              onEachFeature: (feature, fl) => { fl.bindTooltip(feature.properties?.road_name || '', { direction: 'center', className: 'text-[10px] text-gray-400' }); },
            }).addTo(map);
          }
        });
      } else {
        waitForData();
      }

      // Plot previously mapped parcels from OTHER categories as faint context layer
      if (existingMappings.length > 0) {
        const otherMappings = existingMappings.filter(m => m.prcl_ctgry_cd !== selectedCategory);
        if (otherMappings.length > 0) {
          const otherMappedIds = new Set(otherMappings.map((m: any) => Number(m.prcl_id)));
          const otherSvmIds = [...new Set(otherMappings.map((m: any) => Number(m.svm_id)).filter(Boolean))];
          const otherCategories = new Set(otherMappings.map((m: any) => m.prcl_ctgry_cd));

          for (const svmId of otherSvmIds) {
            if (otherCategories.has('roads')) {
              axiosInstance.get(`${BASE_URL}/dashboard/road-network`, { params: { svmId } }).then(res => {
                const geo = res.data?.data || res.data;
                if (geo?.features && map) {
                  L.geoJSON(geo, {
                    filter: (f: any) => otherMappedIds.has(Number(f.properties?.ds_lyr_id || f.properties?.road_id)),
                    style: () => ({ color: '#3b82f6', weight: 2, opacity: 0.3, dashArray: '4,4' }),
                    onEachFeature: (f: any, fl: any) => fl.bindTooltip(`${f.properties?.road_name || 'Road'} (mapped)`, { direction: 'center', className: 'text-[10px] text-gray-400' }),
                  }).addTo(map);
                }
              }).catch(() => {});
            }
            if (otherCategories.has('land_parcels') || otherCategories.has('water_bodies') || otherCategories.has('public_infra')) {
              axiosInstance.get(`${BASE_URL}/dashboard/land-parcels`, { params: { svmId } }).then(res => {
                const geo = res.data?.data || res.data;
                if (geo?.features && map) {
                  L.geoJSON(geo, {
                    filter: (f: any) => otherMappedIds.has(Number(f.properties?.ds_lyr_id || f.properties?.parcel_id)),
                    style: (f: any) => {
                      const cid = Number(f?.properties?.category_id);
                      const colors: Record<number, string> = { 78: '#15803d', 77: '#a16207', 105: '#6d28d9', 98: '#0891b2', 106: '#c2410c' };
                      const c = colors[cid] || '#7c3aed';
                      return { color: c, weight: 1.5, fillColor: c, fillOpacity: 0.15, opacity: 0.4, dashArray: '4,4' };
                    },
                    onEachFeature: (f: any, fl: any) => fl.bindTooltip(`${f.properties?.category || f.properties?.land_usage || 'Parcel'} (mapped)`, { direction: 'center', className: 'text-[10px] text-gray-400' }),
                  }).addTo(map);
                }
              }).catch(() => {});
            }
          }
        }
      }
    }, 150);

    // Watch container size changes and invalidate map
    let resizeObserver: ResizeObserver | undefined;
    if (mapContainerRef.current) {
      resizeObserver = new ResizeObserver(() => mapRef.current?.invalidateSize());
      resizeObserver.observe(mapContainerRef.current);
    }

    return () => {
      clearTimeout(timer);
      resizeObserver?.disconnect();
      if (mapRef.current) { mapRef.current.remove(); mapRef.current = null; }
      parcelLayersMap.current.clear();
    };
  }, [step, selectedSvm, selectedCategory]);

  // ─── Swap base layer when selection changes ──────────────
  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;
    const layer = BASE_LAYERS.find(l => l.key === baseLayerKey) || BASE_LAYERS[0];
    if (tileLayerRef.current) map.removeLayer(tileLayerRef.current);
    tileLayerRef.current = L.tileLayer(layer.url, { attribution: layer.attribution, maxZoom: layer.maxZoom || 19 }).addTo(map);
    tileLayerRef.current.bringToBack();
  }, [baseLayerKey]);

  // ─── Sync styles on hover/selection ───────────────────────

  useEffect(() => {
    if (step !== 3 || !selectedCategory) return;
    const catColor = CATEGORY_CONFIG[selectedCategory].mapColor;
    const isLandParcels = selectedCategory === 'land_parcels';
    const isRoads = selectedCategory === 'roads';
    parcelLayersMap.current.forEach((path, id) => {
      const isSel = selectedParcels.has(id);
      const isHov = id === hoveredParcelId;
      if (isHov && isSel)  { path.setStyle({ color: '#f59e0b', weight: 5, fillColor: '#fef3c7', fillOpacity: 0.5 }); (path as any).bringToFront?.(); (path as any).openTooltip?.(); }
      else if (isHov)      { path.setStyle({ color: '#f59e0b', weight: 4, fillColor: '#fef3c7', fillOpacity: 0.4 }); (path as any).bringToFront?.(); (path as any).openTooltip?.(); }
      else if (isSel)      { path.setStyle({ color: '#059669', weight: 3, fillColor: '#d1fae5', fillOpacity: 0.4 }); }
      else {
        if (isLandParcels) {
          const cid = parcelCategoryMap.current.get(id);
          const lpc = cid ? LAND_PARCEL_COLORS[cid] : undefined;
          if (lpc) { path.setStyle({ color: lpc.color, weight: 2, fillColor: lpc.fill, fillOpacity: 0.35 }); }
          else { path.setStyle({ color: LAND_PARCEL_DEFAULT_COLOR.color, weight: 2, fillColor: LAND_PARCEL_DEFAULT_COLOR.fill, fillOpacity: 0.2 }); }
        } else if (isRoads) {
          const rt = parcelRoadTypeMap.current.get(id);
          const rtc = rt ? ROAD_TYPE_COLORS[rt] : undefined;
          path.setStyle({ color: rtc?.color || ROAD_DEFAULT_COLOR, weight: 3, fillOpacity: 0.15 });
        } else {
          path.setStyle({ color: catColor, weight: 2, fillColor: catColor, fillOpacity: 0.2 });
        }
        (path as any).closeTooltip?.();
      }
    });
  }, [selectedParcels, hoveredParcelId, step, selectedCategory]);

  // ─── Toggle layer visibility based on legend filter ─────
  useEffect(() => {
    const map = mapRef.current;
    if (!map || step !== 3 || !selectedCategory) return;
    const isRoads = selectedCategory === 'roads';
    const isLandParcels = selectedCategory === 'land_parcels';
    if (!isRoads && !isLandParcels) return;

    parcelLayersMap.current.forEach((path, id) => {
      let key: string | undefined;
      if (isRoads) key = parcelRoadTypeMap.current.get(id);
      else if (isLandParcels) {
        const cid = parcelCategoryMap.current.get(id);
        key = cid !== undefined ? String(cid) : undefined;
      }
      const hidden = key !== undefined && hiddenLegendKeys.has(key);
      if (hidden) {
        if (map.hasLayer(path as any)) map.removeLayer(path as any);
      } else {
        if (!map.hasLayer(path as any)) (path as any).addTo(map);
      }
    });
  }, [hiddenLegendKeys, step, selectedCategory]);

  // ─── Drawing: map click handler for proposed roads ────────

  useEffect(() => {
    const map = mapRef.current;
    if (!map || step !== 3 || !drawingActive) return;
    const onClick = (e: L.LeafletMouseEvent) => {
      const latlng: [number, number] = [e.latlng.lat, e.latlng.lng];
      setCurrentDrawnLatLngs(prev => {
        const next = [...prev, latlng];
        // Update polyline on map
        if (drawLayerRef.current) map.removeLayer(drawLayerRef.current);
        drawLayerRef.current = L.polyline(next, { color: '#059669', weight: 4, dashArray: '8,6' }).addTo(map);
        return next;
      });
    };
    map.on('click', onClick);
    map.getContainer().style.cursor = 'crosshair';
    return () => {
      map.off('click', onClick);
      map.getContainer().style.cursor = '';
    };
  }, [drawingActive, step]);

  const finishDrawing = () => {
    if (currentDrawnLatLngs.length < 2) return;
    const id = ++drawIdCounter.current;
    // Calculate length in meters
    let length = 0;
    for (let i = 1; i < currentDrawnLatLngs.length; i++) {
      length += L.latLng(currentDrawnLatLngs[i - 1]).distanceTo(L.latLng(currentDrawnLatLngs[i]));
    }
    const road = { id, name: newRoadName || `Proposed Road ${id}`, constructionType: newRoadType, latlngs: currentDrawnLatLngs, length: Math.round(length) };
    setDrawnRoads(prev => [...prev, road]);

    // Finalize line on map (solid green)
    if (drawLayerRef.current && mapRef.current) {
      mapRef.current.removeLayer(drawLayerRef.current);
      const line = L.polyline(currentDrawnLatLngs, { color: '#059669', weight: 4 }).addTo(mapRef.current);
      line.bindTooltip(`${road.name} (${road.constructionType})`, { permanent: false, className: 'text-xs font-medium' });
      drawnLayersRef.current.set(id, line);
    }
    drawLayerRef.current = null;
    setCurrentDrawnLatLngs([]);
    setNewRoadName('');
    setDrawingActive(false);
  };

  const cancelDrawing = () => {
    if (drawLayerRef.current && mapRef.current) mapRef.current.removeLayer(drawLayerRef.current);
    drawLayerRef.current = null;
    setCurrentDrawnLatLngs([]);
    setDrawingActive(false);
  };

  const removeDrawnRoad = (id: number) => {
    setDrawnRoads(prev => prev.filter(r => r.id !== id));
    const layer = drawnLayersRef.current.get(id);
    if (layer && mapRef.current) mapRef.current.removeLayer(layer);
    drawnLayersRef.current.delete(id);
  };

  const scrollToListItem = (id: number) => {
    const el = listItemRefsMap.current.get(id);
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
  };

  const toggleParcel = (id: number) => {
    setSelectedParcels(prev => { const n = new Set(prev); if (n.has(id)) n.delete(id); else n.add(id); return n; });
  };

  const filteredSvms = svmSearch
    ? allSachivalayams.filter(s => s.svm_nm.toLowerCase().includes(svmSearch.toLowerCase()) || String(s.svm_cd).toLowerCase().includes(svmSearch.toLowerCase()))
    : allSachivalayams;

  const sortedSvms = [...filteredSvms].sort((a, b) => {
    const am = workSachivalayams.some(ws => String(ws.svm_cd) === String(a.svm_cd)) ? 0 : 1;
    const bm = workSachivalayams.some(ws => String(ws.svm_cd) === String(b.svm_cd)) ? 0 : 1;
    return am !== bm ? am - bm : a.svm_nm.localeCompare(b.svm_nm);
  });

  const handleSave = () => {
    if (!selectedSvm || !selectedCategory) return;
    const gisSvmId = getGisSvmId(selectedSvm);
    const selectedItems = parcels.filter(p => selectedParcels.has(p.id));
    // Add drawn proposed roads as items
    const drawnItems = drawnRoads.map(r => ({
      id: -r.id, // negative to distinguish from real parcels
      name: r.name,
      category: `Proposed - ${r.constructionType}`,
      location: `${r.length}m`,
      _drawn: true,
      _latlngs: r.latlngs,
      _constructionType: r.constructionType,
    }));
    onSave({ category: selectedCategory, svmId: gisSvmId, svmName: selectedSvm.svm_nm, roadMode: roadMode || undefined, items: [...selectedItems, ...drawnItems] });
  };

  if (!isOpen) return null;

  return createPortal(
    <div className="fixed inset-0 z-[9999] flex items-center justify-center">
      <div className="absolute inset-0 bg-black/40" onClick={onClose} />
      <div className={`relative bg-white shadow-2xl flex flex-col transition-all duration-300 ${
        isFullscreen ? 'w-full h-full rounded-none' : 'w-full max-w-5xl mx-4 h-[85vh] rounded-xl'
      }`}>
        {/* Slim Header */}
        <div className={`${WMS_POPUP_HEADER} ${isFullscreen ? '' : 'rounded-t-xl'}`}>
          <div className="flex items-center gap-2 min-w-0">
            <i className="ri-road-map-line text-base text-white/90" />
            <h2 className={WMS_POPUP_TITLE}>GIS Asset Mapping</h2>
          </div>
          <div className="flex items-center gap-2">
            <div className="flex items-center gap-1 text-[11px] text-white/60">
              <span className={step >= 1 ? 'text-white font-medium' : ''}>Sachivalayam</span>
              <i className="ri-arrow-right-s-line" />
              <span className={step >= 2 ? 'text-white font-medium' : ''}>Category</span>
              <i className="ri-arrow-right-s-line" />
              <span className={step >= 3 ? 'text-white font-medium' : ''}>Select</span>
            </div>
            <button onClick={() => setIsFullscreen(f => !f)} className={`${WMS_POPUP_CLOSE} ml-1`} title={isFullscreen ? 'Exit fullscreen' : 'Fullscreen'}>
              <i className={`${isFullscreen ? 'ri-fullscreen-exit-line' : 'ri-fullscreen-line'} text-base`} />
            </button>
            <button onClick={onClose} className={WMS_POPUP_CLOSE}><i className="ri-close-line text-lg" /></button>
          </div>
        </div>

        {/* Work Details Card */}
        <div className="flex-shrink-0 px-5 py-3 bg-white border-b">
          <WorkDetailsCard
            workCode={workCode}
            workName={workName}
            workStatus={workStatus}
            workType={workType}
            workCategory={workCategory}
            udaName={udaName}
            schemeName={schemeName}
            sanctionAmount={sanctionAmount}
            onWorkCodeClick={onOpenWorkProfile && workId ? () => onOpenWorkProfile(workId) : undefined}
          />
        </div>

        {/* Body */}
        <div className="flex-1 flex overflow-hidden">

          {/* Step 1: Sachivalayam */}
          {step === 1 && (
            <div className="flex-1 flex flex-col">
              {svmLoading ? (
                <div className="flex-1 p-4"><ListSkeleton rows={4} avatar={false} /></div>
              ) : workSachivalayams.length > 0 ? (
                /* Work has mapped sachivalayams — show them as primary options */
                <div className="flex-1 flex flex-col overflow-auto">
                  <div className="px-4 py-3 border-b">
                    <p className="text-xs font-semibold text-gray-700"><i className="ri-map-pin-2-fill text-emerald-500 mr-1" />Mapped Sachivalayams</p>
                    <p className="text-[10px] text-gray-400 mt-0.5">Select a sachivalayam mapped to this work</p>
                  </div>
                  <div className="px-3 py-3">
                    <div className="grid grid-cols-2 gap-2">
                      {workSachivalayams.map(ws => {
                        // Find the full sachivalayam from loaded list for the click handler
                        const fullSvm = allSachivalayams.find(s => String(s.svm_cd) === String(ws.svm_cd));
                        const svm = fullSvm || { svm_id: ws.svm_id, svm_cd: ws.svm_cd, svm_nm: ws.svm_nm };
                        return (
                          <button key={ws.svm_id} onClick={() => handleSvmSelect(svm)}
                            className="flex items-center gap-3 p-3.5 rounded-xl border-2 border-emerald-200 bg-emerald-50 text-left transition-all hover:shadow-lg hover:border-emerald-400">
                            <div className="w-10 h-10 rounded-xl flex items-center justify-center bg-emerald-100 text-emerald-600">
                              <i className="ri-map-pin-2-fill text-lg" />
                            </div>
                            <div className="flex-1 min-w-0">
                              <p className="text-sm font-semibold text-gray-800 truncate">{ws.svm_nm}</p>
                              <p className="text-xs text-gray-800 font-medium">{ws.svm_cd}</p>
                            </div>
                            <i className="ri-arrow-right-s-line text-emerald-400 text-lg" />
                          </button>
                        );
                      })}
                    </div>
                  </div>

                  {/* Divider + Show all option */}
                  <div className="px-4 py-2 border-t border-b bg-gray-50">
                    <p className="text-[10px] text-gray-400 flex items-center gap-1">
                      <i className="ri-list-unordered" /> Or select from all {allSachivalayams.length} sachivalayams in this UDA
                    </p>
                  </div>
                  <div className="px-4 py-2 border-b">
                    <div className="relative">
                      <i className="ri-search-line absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400 text-sm" />
                      <input type="text" placeholder="Search all sachivalayams..." value={svmSearch} onChange={e => setSvmSearch(e.target.value)}
                        className="pl-8 pr-3 py-1.5 border rounded-lg text-xs w-full focus:outline-none focus:ring-1 focus:ring-emerald-300" />
                    </div>
                  </div>
                  <div className="flex-1 overflow-auto px-3 py-2">
                    <div className="grid grid-cols-2 gap-1.5">
                      {sortedSvms.filter(s => !workSachivalayams.some(ws => String(ws.svm_cd) === String(s.svm_cd))).map(svm => (
                        <button key={svm.svm_id} onClick={() => handleSvmSelect(svm)}
                          className="flex items-center gap-2 p-2 rounded-lg border border-gray-300 bg-gray-50/80 text-left transition-all hover:shadow-sm hover:border-gray-400 hover:bg-white">
                          <div className="w-6 h-6 rounded flex items-center justify-center bg-gray-100 text-gray-400">
                            <i className="ri-map-pin-2-line text-xs" />
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="text-xs font-medium text-gray-700 truncate">{svm.svm_nm}</p>
                            <p className="text-[9px] text-gray-800 font-medium">{svm.svm_cd}</p>
                          </div>
                          <i className="ri-arrow-right-s-line text-gray-300 text-sm" />
                        </button>
                      ))}
                    </div>
                  </div>
                </div>
              ) : (
                /* No mapped sachivalayams — show full list */
                <div className="flex-1 flex flex-col">
                  <div className="px-4 py-3 border-b">
                    <div className="relative">
                      <i className="ri-search-line absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400 text-sm" />
                      <input type="text" placeholder="Search sachivalayams..." value={svmSearch} onChange={e => setSvmSearch(e.target.value)}
                        className="pl-8 pr-3 py-1.5 border rounded-lg text-sm w-full focus:outline-none focus:ring-1 focus:ring-emerald-300" />
                    </div>
                    <p className="text-xs text-gray-500 mt-1.5">{sortedSvms.length} sachivalayams — select one to proceed</p>
                  </div>
                  <div className="flex-1 overflow-auto px-3 py-2">
                    <div className="grid grid-cols-2 gap-2">
                      {sortedSvms.map(svm => (
                        <button key={svm.svm_id} onClick={() => handleSvmSelect(svm)}
                          className="flex items-center gap-3 p-3 rounded-lg border border-gray-300 bg-gray-50/80 text-left transition-all hover:shadow-md hover:border-gray-400 hover:bg-white">
                          <div className="w-8 h-8 rounded-lg flex items-center justify-center bg-gray-100 text-gray-400">
                            <i className="ri-map-pin-2-line text-sm" />
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-medium text-gray-800 truncate">{svm.svm_nm}</p>
                            <p className="text-[10px] text-gray-800 font-medium">{svm.svm_cd}</p>
                          </div>
                          <i className="ri-arrow-right-s-line text-gray-400" />
                        </button>
                      ))}
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Step 2: Category */}
          {step === 2 && selectedSvm && (
            <div className="flex-1 flex flex-col">
              <div className="px-4 py-3 border-b flex items-center gap-2">
                <button onClick={() => setStep(1)} className="text-gray-400 hover:text-gray-600"><i className="ri-arrow-left-line text-lg" /></button>
                <div>
                  <p className="text-sm font-semibold text-gray-800">{selectedSvm.svm_nm} - <span className="text-gray-400 font-normal">{selectedSvm.svm_cd}</span></p>
                  <p className="text-xs text-gray-500">Select asset category</p>
                </div>
              </div>
              <div className="flex-1 overflow-auto px-3 py-4">
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                  {(Object.entries(CATEGORY_CONFIG) as [AssetCategory, typeof CATEGORY_CONFIG.roads][]).map(([key, cfg]) => {
                    const s = infraSummary;
                    let count = 0, detail = '';
                    if (key === 'roads' && s?.roads) { count = s.roads.count; detail = `${(s.roads.totalLength / 1000).toFixed(1)} km`; }
                    else if (key === 'water_bodies' && s?.waterBodies) { count = s.waterBodies.count; detail = `${s.waterBodies.totalArea.toFixed(1)} acres`; }
                    else if (key === 'public_infra' && s?.publicInfra) { count = s.publicInfra.totalCount; detail = `${s.publicInfra.categories?.length || 0} types`; }
                    else if (key === 'land_parcels' && s?.landParcels) { count = s.landParcels.count; detail = 'All land parcels'; }
                    const mappedInCategory = existingMappings.filter(m => m.prcl_ctgry_cd === key).length;
                    return (
                      <button key={key} onClick={() => handleCategorySelect(key as AssetCategory)}
                        className={`flex items-center gap-3 px-4 py-3 rounded-xl border-2 transition-all hover:shadow-lg ${cfg.bgClass} relative text-left`}>
                        {mappedInCategory > 0 && (
                          <span className="absolute top-1.5 right-1.5 inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded-full bg-emerald-500 text-white text-[10px] font-bold">
                            <i className="ri-check-line text-[10px]" /> {mappedInCategory}
                          </span>
                        )}
                        <div className="w-10 h-10 rounded-lg flex items-center justify-center bg-white shadow-sm shrink-0">
                          <i className={`${cfg.icon} text-lg ${cfg.color}`} />
                        </div>
                        <div className="flex-1 min-w-0">
                          <span className="text-xs font-semibold text-gray-800">{cfg.label}</span>
                          {infraSummary && <><p className="text-lg font-bold text-gray-900">{count}</p>{detail && <p className="text-sm font-bold text-gray-900">({detail})</p>}</>}
                        </div>
                        <i className="ri-arrow-right-s-line text-gray-400" />
                      </button>
                    );
                  })}
                </div>

                {/* Existing Mapped Geographies */}
                {existingMappings.length > 0 && (
                  <div className="mt-4">
                    <div className="flex items-center gap-2 mb-3">
                      <i className="ri-map-pin-line text-emerald-500" />
                      <span className="text-xs font-semibold text-gray-700">Existing Mapped Geographies</span>
                      <span className="text-[10px] text-gray-400">({existingMappings.length} total)</span>
                      <div className="ml-auto flex items-center bg-gray-100 rounded-md p-0.5">
                        <button
                          onClick={() => setExistingView('list')}
                          className={`px-2 py-1 rounded text-[10px] font-medium transition-all ${existingView === 'list' ? 'bg-white shadow-sm text-gray-700' : 'text-gray-400 hover:text-gray-600'}`}
                        >
                          <i className="ri-list-unordered mr-0.5" /> List
                        </button>
                        <button
                          onClick={() => setExistingView('map')}
                          className={`px-2 py-1 rounded text-[10px] font-medium transition-all ${existingView === 'map' ? 'bg-white shadow-sm text-gray-700' : 'text-gray-400 hover:text-gray-600'}`}
                        >
                          <i className="ri-map-2-line mr-0.5" /> Map
                        </button>
                      </div>
                    </div>

                    {existingView === 'list' ? (
                      <div className="grid grid-cols-1 gap-1.5">
                        {(Object.entries(CATEGORY_CONFIG) as [AssetCategory, typeof CATEGORY_CONFIG.roads][]).map(([catKey, cfg]) => {
                          const catMappings = existingMappings.filter(m => m.prcl_ctgry_cd === catKey);
                          if (catMappings.length === 0) return null;
                          return (
                            <div key={catKey} className="rounded-lg border border-gray-200 overflow-hidden">
                              <div className="px-3 py-1.5 bg-gray-50 flex items-center gap-2">
                                <i className={`${cfg.icon} ${cfg.color} text-sm`} />
                                <span className="text-xs font-semibold text-gray-700">{cfg.label}</span>
                                <span className="text-[10px] text-emerald-600 font-medium ml-auto">{catMappings.length} mapped</span>
                              </div>
                              <div className="px-3 py-1.5 flex flex-wrap gap-1.5">
                                {catMappings.map(m => (
                                  <span key={m.wrk_prcl_rel_id} className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-emerald-50 border border-emerald-200 text-[10px] text-emerald-700 font-medium">
                                    <i className="ri-map-pin-2-fill text-emerald-400 text-[10px]" />
                                    {m.prcl_nm || `#${m.prcl_id}`}
                                    {m.prcl_sub_ctgry && <span className="text-gray-400 font-normal">· {m.prcl_sub_ctgry}</span>}
                                    {m.svm_nm && <span className="text-gray-400 font-normal">· {m.svm_nm}</span>}
                                  </span>
                                ))}
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    ) : (
                      <div className="rounded-lg border border-gray-200 overflow-hidden">
                        <div ref={existingMapContainerRef} className="h-64 sm:h-80" />
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Step 2.5: Road Mode Selection */}
          {step === (2.5 as any) && selectedSvm && selectedCategory === 'roads' && (
            <div className="flex-1 flex flex-col">
              <div className="px-4 py-3 border-b flex items-center gap-2">
                <button onClick={() => { setStep(2); setSelectedCategory(null); setRoadMode(null); }} className="text-gray-400 hover:text-gray-600"><i className="ri-arrow-left-line text-lg" /></button>
                <div>
                  <p className="text-sm font-semibold text-gray-800">{selectedSvm.svm_nm} - <span className="text-gray-400 font-normal">{selectedSvm.svm_cd}</span></p>
                  <p className="text-xs text-gray-500">Roads — select type</p>
                </div>
              </div>
              <div className="flex-1 overflow-auto p-6">
                <div className="grid grid-cols-3 gap-4 max-w-2xl mx-auto">
                  <button onClick={() => handleRoadModeSelect('existing')}
                    className="flex flex-col items-center gap-3 p-6 rounded-xl border-2 border-blue-200 bg-blue-50 hover:bg-blue-100 transition-all hover:shadow-lg">
                    <div className="w-14 h-14 rounded-xl flex items-center justify-center bg-white shadow-sm">
                      <i className="ri-route-line text-2xl text-blue-600" />
                    </div>
                    <span className="text-sm font-semibold text-gray-800">Existing Road</span>
                    <p className="text-[10px] text-gray-500 text-center">Select from existing roads in the sachivalayam</p>
                    <i className="ri-arrow-right-line text-gray-400" />
                  </button>

                  <button onClick={() => handleRoadModeSelect('proposed')}
                    className="flex flex-col items-center gap-3 p-6 rounded-xl border-2 border-emerald-200 bg-emerald-50 hover:bg-emerald-100 transition-all hover:shadow-lg">
                    <div className="w-14 h-14 rounded-xl flex items-center justify-center bg-white shadow-sm">
                      <i className="ri-draft-line text-2xl text-emerald-600" />
                    </div>
                    <span className="text-sm font-semibold text-gray-800">Proposed New Road</span>
                    <p className="text-[10px] text-gray-500 text-center">Draw a new proposed road on the map</p>
                    <i className="ri-arrow-right-line text-gray-400" />
                  </button>

                  <button onClick={() => handleRoadModeSelect('both')}
                    className="flex flex-col items-center gap-3 p-6 rounded-xl border-2 border-purple-200 bg-purple-50 hover:bg-purple-100 transition-all hover:shadow-lg">
                    <div className="w-14 h-14 rounded-xl flex items-center justify-center bg-white shadow-sm">
                      <i className="ri-git-merge-line text-2xl text-purple-600" />
                    </div>
                    <span className="text-sm font-semibold text-gray-800">Both</span>
                    <p className="text-[10px] text-gray-500 text-center">Select existing roads and draw new proposed roads</p>
                    <i className="ri-arrow-right-line text-gray-400" />
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Step 3: Parcels + Map */}
          {step === 3 && selectedSvm && selectedCategory && (
            <div className="flex-1 flex overflow-hidden" ref={splitContainerRef}>
              {/* Left: List */}
              <div style={{ width: `${splitPercent}%` }} className="flex flex-col shrink-0">
                <div className="px-3 py-2 border-b flex items-center gap-2 bg-gradient-to-r from-slate-700 to-slate-600">
                  <button onClick={() => { setStep(2); setSelectedCategory(null); setParcels([]); setSelectedParcels(new Set()); setRoadMode(null); }} className="text-white/60 hover:text-white"><i className="ri-arrow-left-line" /></button>
                  <div className="flex-1">
                    <p className="text-xs font-semibold text-white flex items-center gap-1">
                      <i className={`${CATEGORY_CONFIG[selectedCategory].icon}`} />
                      {CATEGORY_CONFIG[selectedCategory].label}
                    </p>
                    <p className="text-[10px] text-slate-300">{selectedSvm.svm_nm} — {parcels.length} items{drawnRoads.length > 0 ? ` + ${drawnRoads.length} proposed` : ''}</p>
                  </div>
                  <span className="text-xs text-emerald-300 font-medium">{selectedParcels.size} selected</span>
                </div>
                <div className="flex-1 overflow-auto px-2 py-1">
                  {/* Proposed Road Drawing (for roads category) */}
                  {selectedCategory === 'roads' && (showProposedForm || drawingActive || drawnRoads.length > 0) && (
                    <div className="border-b pb-2 mb-2 px-1">
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-xs font-semibold text-gray-700"><i className="ri-pencil-ruler-2-line mr-1 text-emerald-500" />New Proposed Road</span>
                        <div className="flex items-center gap-2">
                          {drawnRoads.length > 0 && <span className="text-[10px] text-emerald-600 font-medium">{drawnRoads.length} drawn</span>}
                          {!drawingActive && <button onClick={() => setShowProposedForm(false)} className="text-gray-400 hover:text-gray-600"><i className="ri-close-line text-sm" /></button>}
                        </div>
                      </div>

                      {drawingActive ? (
                        <div className="space-y-2">
                          <p className="text-[10px] text-blue-600 bg-blue-50 rounded px-2 py-1">
                            <i className="ri-cursor-line mr-1" />Click on map to add points ({currentDrawnLatLngs.length} points)
                          </p>
                          <div className="flex gap-1">
                            <button onClick={finishDrawing} disabled={currentDrawnLatLngs.length < 2}
                              className="flex-1 px-2 py-1.5 rounded text-xs font-medium bg-emerald-50 text-emerald-700 hover:bg-emerald-100 disabled:opacity-40 disabled:cursor-not-allowed">
                              <i className="ri-check-line mr-1" />Finish
                            </button>
                            <button onClick={cancelDrawing}
                              className="px-2 py-1.5 rounded text-xs font-medium bg-red-50 text-red-600 hover:bg-red-100">
                              <i className="ri-close-line mr-1" />Cancel
                            </button>
                          </div>
                        </div>
                      ) : (
                        <div className="space-y-2">
                          <input type="text" placeholder="Road name..." value={newRoadName} onChange={e => setNewRoadName(e.target.value)}
                            className="w-full border rounded-lg text-xs px-2.5 py-1.5 focus:outline-none focus:ring-1 focus:ring-emerald-300" />
                          <select value={newRoadType} onChange={e => setNewRoadType(e.target.value)}
                            className="w-full border rounded-lg text-xs px-2.5 py-1.5 focus:outline-none focus:ring-1 focus:ring-emerald-300">
                            {ROAD_CONSTRUCTION_TYPES.map(t => <option key={t.id} value={t.name}>{t.name}</option>)}
                          </select>
                          <button onClick={() => setDrawingActive(true)}
                            className="w-full px-2 py-1.5 rounded-lg text-xs font-medium bg-emerald-600 text-white hover:bg-emerald-700 flex items-center justify-center gap-1">
                            <i className="ri-pencil-line" /> Start Drawing
                          </button>
                        </div>
                      )}

                      {/* Drawn roads list */}
                      {drawnRoads.length > 0 && (
                        <div className="mt-2 space-y-1">
                          {drawnRoads.map(r => (
                            <div key={r.id} className="flex items-center gap-2 px-2 py-1.5 bg-emerald-50 rounded-lg border border-emerald-200">
                              <i className="ri-route-line text-emerald-600 text-sm" />
                              <div className="flex-1 min-w-0">
                                <p className="text-xs font-medium text-emerald-700 truncate">{r.name}</p>
                                <p className="text-[10px] text-gray-500">{r.constructionType} · {r.length}m · {r.latlngs.length} pts</p>
                              </div>
                              <button onClick={() => removeDrawnRoad(r.id)} className="text-red-400 hover:text-red-600"><i className="ri-delete-bin-line text-sm" /></button>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  )}
                  {/* + New Proposed Road button (for roads, when form is hidden) */}
                  {selectedCategory === 'roads' && !showProposedForm && !drawingActive && drawnRoads.length === 0 && (
                    <button onClick={() => setShowProposedForm(true)}
                      className="w-full mb-2 px-2 py-1.5 rounded-lg text-xs font-medium border border-dashed border-emerald-400 text-emerald-600 hover:bg-emerald-50 flex items-center justify-center gap-1">
                      <i className="ri-add-line" /> New Proposed Road
                    </button>
                  )}
                  {parcelsLoading ? (
                    <ListSkeleton rows={3} avatar={false} />
                  ) : parcels.length === 0 ? (
                    <div className="flex items-center justify-center h-32 text-gray-400 text-sm">No parcels found</div>
                  ) : (
                    <div className="space-y-0.5">
                      {parcels.filter(p => {
                        if (hiddenLegendKeys.size === 0) return true;
                        if (selectedCategory === 'roads' && p.roadType) {
                          return !hiddenLegendKeys.has(p.roadType);
                        }
                        if (selectedCategory === 'land_parcels' && p.categoryId !== undefined) {
                          return !hiddenLegendKeys.has(String(p.categoryId));
                        }
                        return true;
                      }).map(p => {
                        const isSel = selectedParcels.has(p.id);
                        const isHov = hoveredParcelId === p.id;
                        return (
                          <label key={p.id}
                            ref={el => { if (el) listItemRefsMap.current.set(p.id, el); }}
                            onMouseEnter={() => setHoveredParcelId(p.id)}
                            onMouseLeave={() => setHoveredParcelId(null)}
                            className={`flex items-center gap-2.5 px-2.5 py-2 rounded-lg cursor-pointer transition-all duration-150 ${
                              isHov ? 'bg-amber-50 border border-amber-300 shadow-sm'
                              : isSel ? 'bg-emerald-50 border border-emerald-200'
                              : 'hover:bg-gray-50 border border-transparent'
                            }`}>
                            <input type="checkbox" checked={isSel} onChange={() => toggleParcel(p.id)}
                              className="w-3.5 h-3.5 rounded border-gray-300 text-emerald-600 focus:ring-emerald-500" />
                            <div className="flex-1 min-w-0">
                              <p className={`text-xs font-medium leading-tight ${isHov ? 'text-amber-800' : isSel ? 'text-emerald-700' : 'text-gray-700'}`}>
                                {p.name || `Parcel #${p.id}`}
                              </p>
                              <p className="text-[11px] text-gray-600 truncate">{p.category && <><span className="font-medium text-gray-700">{p.category}</span> · </>}{p.location || ''}</p>
                            </div>
                            {isSel && <i className="ri-checkbox-circle-fill text-emerald-500 text-sm" />}
                            <button
                              type="button"
                              title="Zoom to parcel"
                              onClick={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                const layer = parcelLayersMap.current.get(p.id);
                                if (layer && mapRef.current) {
                                  mapRef.current.fitBounds((layer as any).getBounds(), { padding: [40, 40], maxZoom: 18 });
                                }
                              }}
                              className="ml-auto shrink-0 w-6 h-6 flex items-center justify-center rounded hover:bg-indigo-100 text-gray-400 hover:text-indigo-600 transition-colors"
                            >
                              <i className="ri-focus-3-line text-sm" />
                            </button>
                          </label>
                        );
                      })}
                    </div>
                  )}
                </div>
              </div>
              {/* Resizable Divider */}
              <div
                onMouseDown={handleDragStart}
                className="w-1.5 cursor-col-resize bg-gray-200 hover:bg-emerald-400 active:bg-emerald-500 transition-colors shrink-0 relative group"
                title="Drag to resize"
              >
                <div className="absolute inset-y-0 -left-1 -right-1" />
                <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-1 h-8 rounded-full bg-gray-400 group-hover:bg-white transition-colors" />
              </div>

              {/* Right: Map */}
              <div className="flex-1 flex flex-col min-w-0">
                <div className="px-3 py-1.5 bg-gray-50 border-b flex items-center gap-1.5 flex-wrap">
                  <i className="ri-map-2-line text-emerald-500 text-sm" />
                  <span className="text-[11px] font-medium text-gray-600">{selectedSvm.svm_nm}</span>
                  <span className="text-[10px] text-gray-400 ml-1">— select on map</span>
                  {selectedCategory === 'roads' && (
                    <div className="flex items-center gap-1.5 ml-2 flex-wrap">
                      {Object.entries(ROAD_TYPE_COLORS).map(([key, rtc]) => {
                        const isHidden = hiddenLegendKeys.has(key);
                        return (
                          <button
                            key={key}
                            type="button"
                            onClick={() => setHiddenLegendKeys(prev => {
                              const next = new Set(prev);
                              if (next.has(key)) next.delete(key); else next.add(key);
                              return next;
                            })}
                            className={`flex items-center gap-1 text-[10px] px-1.5 py-0.5 rounded-full border transition-all cursor-pointer ${
                              isHidden ? 'opacity-40 line-through border-gray-200 bg-gray-50' : 'border-gray-300 bg-white hover:shadow-sm'
                            }`}
                          >
                            <span className="w-3 h-0.5 rounded" style={{ backgroundColor: isHidden ? '#cbd5e1' : rtc.color }} />
                            {rtc.label}
                          </button>
                        );
                      })}
                    </div>
                  )}
                  {selectedCategory === 'land_parcels' && (
                    <div className="flex items-center gap-1.5 ml-2 flex-wrap">
                      {Object.entries(LAND_PARCEL_COLORS).map(([key, lpc]) => {
                        const isHidden = hiddenLegendKeys.has(key);
                        return (
                          <button
                            key={key}
                            type="button"
                            onClick={() => setHiddenLegendKeys(prev => {
                              const next = new Set(prev);
                              if (next.has(key)) next.delete(key); else next.add(key);
                              return next;
                            })}
                            className={`flex items-center gap-1 text-[10px] px-1.5 py-0.5 rounded-full border transition-all cursor-pointer ${
                              isHidden ? 'opacity-40 line-through border-gray-200 bg-gray-50' : 'border-gray-300 bg-white hover:shadow-sm'
                            }`}
                          >
                            <span className="w-2.5 h-2.5 rounded-sm border" style={{
                              backgroundColor: isHidden ? '#e2e8f0' : lpc.fill,
                              borderColor: isHidden ? '#cbd5e1' : lpc.color,
                            }} />
                            {lpc.label}
                          </button>
                        );
                      })}
                    </div>
                  )}
                  <div className="ml-auto flex items-center gap-2">
                    <select
                      value={baseLayerKey}
                      onChange={e => setBaseLayerKey(e.target.value)}
                      className="text-[10px] border rounded px-1.5 py-0.5 bg-white text-gray-600 focus:outline-none focus:ring-1 focus:ring-emerald-300 cursor-pointer"
                    >
                      {BASE_LAYERS.map(l => (
                        <option key={l.key} value={l.key}>{l.label}</option>
                      ))}
                    </select>
                    <button
                      type="button"
                      title="Zoom to all parcels"
                      onClick={() => {
                        if (mapRef.current && parcelLayersMap.current.size > 0) {
                          const group = L.featureGroup(Array.from(parcelLayersMap.current.values()));
                          mapRef.current.fitBounds(group.getBounds(), { padding: [20, 20] });
                        }
                      }}
                      className="shrink-0 px-2 py-0.5 rounded text-[10px] font-medium text-emerald-600 hover:bg-emerald-100 transition-colors flex items-center gap-1"
                    >
                      <i className="ri-fullscreen-line text-xs" /> Zoom to all
                    </button>
                  </div>
                </div>
                <div ref={mapContainerRef} className="flex-1" />
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className={`flex items-center justify-between px-5 py-3 border-t bg-gray-50 ${isFullscreen ? '' : 'rounded-b-xl'}`}>
          <span className="text-xs text-gray-500">
            {step === 3
              ? `${selectedParcels.size} parcel(s) selected${drawnRoads.length > 0 ? ` + ${drawnRoads.length} drawn road(s)` : ''}`
              : step === 2 ? 'Select a category' : 'Select a sachivalayam'}
          </span>
          <div className="flex items-center gap-2">
            <button onClick={onClose} className="px-4 py-2 text-sm text-gray-600 hover:text-gray-800 rounded-lg hover:bg-gray-100">Cancel</button>
            {step === 3 && (selectedParcels.size > 0 || drawnRoads.length > 0) && (
              <button onClick={handleSave} disabled={saving}
                className="px-4 py-2 text-sm text-white bg-emerald-600 hover:bg-emerald-700 rounded-lg disabled:opacity-50 flex items-center gap-1.5">
                {saving ? <><div className="animate-spin rounded-full h-3.5 w-3.5 border-b-2 border-white" /> Saving...</> : <><i className="ri-save-line" /> Save Selection</>}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
}
